import plainDayjs from 'dayjs';
import type { ConfigType, Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import localeData from 'dayjs/plugin/localeData';
import {
    DAY_IN_SECONDS,
    HOUR_IN_SECONDS,
    MINUTE_IN_SECONDS
} from 'tradera/constants/time';
import 'dayjs/locale/sv';
import 'dayjs/locale/da';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';

plainDayjs.extend(isBetween);
plainDayjs.extend(isSameOrAfter);
plainDayjs.extend(utc);
plainDayjs.extend(timezone);
plainDayjs.extend(localizedFormat);
plainDayjs.extend(relativeTime);
plainDayjs.extend(localeData);

export const dayjs = plainDayjs;

export const localizedDayjs =
    (locale: string) =>
    (...args: Parameters<typeof dayjs>): Dayjs =>
        dayjs(...args).locale(locale);

const swedishTimeZone = 'Europe/Stockholm';

export const swedishTimeZoneDate = (
    date: ConfigType,
    localizedDayjsFn: ReturnType<typeof localizedDayjs>
): Dayjs => {
    const locale = localizedDayjsFn().locale();
    return localizedDayjsFn(date).tz(swedishTimeZone).locale(locale);
};

export const formatUtcTimeAsSwedishTimeZone = (
    date: ConfigType,
    localizedDayjsFn: ReturnType<typeof localizedDayjs>
): string => {
    return swedishTimeZoneDate(date, localizedDayjsFn).format('HH:mm');
};

export type FormatOptions = {
    includeWeekday?: boolean;
    includeYear?: boolean;
    excludeTime?: boolean;
};

export const formatUtcDateAsSwedishTimeZoneDate = (
    date: ConfigType,
    localizedDayjsFn: ReturnType<typeof localizedDayjs>,
    options: FormatOptions = {}
): string => {
    const formatArray = [
        options.includeWeekday && 'ddd', // tor
        'D MMM', // 19 okt
        options.includeYear && 'YYYY', // 2023
        !options.excludeTime && 'HH:mm' // 09:50
    ];
    const format = formatArray.filter(Boolean).join(' ');

    return swedishTimeZoneDate(date, localizedDayjsFn).format(format);
};

export const getCurrentSwedishTimeZoneDate = (): string =>
    dayjs().tz(swedishTimeZone).format('YYYYMMDD');

type TimeSpanOptions = {
    startUnit?: string;
    endUnit?: string;
};

export const getTimeSpanParts = (
    seconds: number,
    options: TimeSpanOptions = {}
): Record<string, number> => {
    const optionsWithDefaults = {
        startUnit: '',
        endUnit: '',
        ...options
    };
    const parts: Record<string, number> = {};
    let remaining = seconds;
    let started = !optionsWithDefaults.startUnit;
    for (const [part, length] of Object.entries(TIME_SPAN_UNIT_LENGTHS)) {
        if (!started && part === optionsWithDefaults.startUnit) started = true;
        if (started) {
            if (remaining >= length) {
                parts[part] = Math.floor(remaining / length);
            }
            // eslint-disable-next-line better-mutation/no-mutation
            remaining %= length;
            if (remaining === 0 || optionsWithDefaults.endUnit === part) break;
        }
    }
    return parts;
};

export const TIME_SPAN_UNITS = {
    DAYS: 'days',
    HOURS: 'hours',
    MINUTES: 'minutes',
    SECONDS: 'seconds'
} as const;

type TimeSpanUnit = (typeof TIME_SPAN_UNITS)[keyof typeof TIME_SPAN_UNITS];

const TIME_SPAN_UNIT_LENGTHS: Record<TimeSpanUnit, number> = {
    [TIME_SPAN_UNITS.DAYS]: DAY_IN_SECONDS,
    [TIME_SPAN_UNITS.HOURS]: HOUR_IN_SECONDS,
    [TIME_SPAN_UNITS.MINUTES]: MINUTE_IN_SECONDS,
    [TIME_SPAN_UNITS.SECONDS]: 1
};

export const getMonths = (locale: string): string[] => {
    dayjs.locale(locale);
    return dayjs.months();
};
